import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TextField,
  Button,
  Tabs,
  Tab,
  Chip,
  Alert,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useRouter } from "../../../hooks/useRouter";
import { formatCurrency } from "../../../utils";
import FinanceContainer from "../containers/FinanceContainer";
import config from "../../../config";

interface AgingRow {
  date: Date;
  paymentNeeded: number;
  paymentsMade: number;
  runningBalance: number;
}

interface AgingBand {
  [key: string]: number;
  total?: number;
}

interface ScheduleAgingData {
  rows: AgingRow[];
  lastBalance: number;
  agingBands: AgingBand;
  hasData: boolean;
}

interface DebtorExcelRecord {
  debtorRef: string;
  paymentSchedule: ScheduleAgingData;
  vatSchedule: ScheduleAgingData;
  combined: {
    outstanding: number;
  };
}

interface ExcelAgingResponse {
  asAt: string;
  debtorCount: number;
  summary: {
    totalPaymentOutstanding: number;
    totalVatOutstanding: number;
    totalCombinedOutstanding: number;
  };
  debtors: {
    [key: string]: DebtorExcelRecord;
  };
}

const ComprehensiveAgingReport: React.FC = () => {
  const router = useRouter();
  const [data, setData] = useState<ExcelAgingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [asAtDate, setAsAtDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [excelPath, setExcelPath] = useState<string>("");

  // Query parameter for Excel file path
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pathParam = params.get("excelPath");
    if (pathParam) {
      setExcelPath(pathParam);
    }
  }, []);

  useEffect(() => {
    // Only fetch if excelPath is provided
    if (excelPath) {
      fetchAgingReport();
    }
  }, [asAtDate, excelPath]);

  const fetchAgingReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("vms_access_token");
      
      // Build URL with Excel file path if provided
      let url = `${config.API_BASE_URL}/debtors/aging-from-excel?asAtDate=${asAtDate}`;
      if (excelPath) {
        url += `&excelPath=${encodeURIComponent(excelPath)}`;
      }
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        const errorText = await response.text();
        setError(`Error loading aging report: ${errorText}`);
      }
    } catch (error) {
      console.error("Error fetching aging report:", error);
      setError(`Failed to fetch aging data: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAgingReport();
  };

  const handleDownloadCSV = () => {
    if (!data) return;

    const debtorRecords = Object.values(data.debtors);
    const headers = [
      "Debtor Ref",
      "Payment Schedule - Last Balance",
      "Payment Schedule - Has Data",
      "Payment Outstanding",
      "Payment - Current",
      "Payment - 30 Days",
      "Payment - 60 Days",
      "Payment - 90 Days",
      "Payment - 120+",
      "VAT Schedule - Last Balance",
      "VAT - Has Data",
      "VAT Outstanding",
      "VAT - Current",
      "VAT - 30 Days",
      "VAT - 60 Days",
      "VAT - 90 Days",
      "VAT - 120+",
      "Combined Outstanding",
    ];

    const csvRows = [
      headers.join(","),
      ...debtorRecords.map((record) =>
        [
          `"${record.debtorRef}"`,
          record.paymentSchedule.lastBalance,
          record.paymentSchedule.hasData,
          record.paymentSchedule.agingBands.total,
          record.paymentSchedule.agingBands["Current"] || 0,
          record.paymentSchedule.agingBands["30 Days"] || 0,
          record.paymentSchedule.agingBands["60 Days"] || 0,
          record.paymentSchedule.agingBands["90 Days"] || 0,
          record.paymentSchedule.agingBands["120+"] || 0,
          record.vatSchedule.lastBalance,
          record.vatSchedule.hasData,
          record.vatSchedule.agingBands.total,
          record.vatSchedule.agingBands["Current"] || 0,
          record.vatSchedule.agingBands["30 Days"] || 0,
          record.vatSchedule.agingBands["60 Days"] || 0,
          record.vatSchedule.agingBands["90 Days"] || 0,
          record.vatSchedule.agingBands["120+"] || 0,
          record.combined.outstanding,
        ].join(",")
      ),
    ];

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `comprehensive-aging-${asAtDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getAgingColor = (band: string): string => {
    if (band === "120+") return "#ffebee";
    if (band === "90 Days") return "#fff3e0";
    if (band === "60 Days") return "#fffde7";
    if (band === "30 Days") return "#e3f2fd";
    return "#e8f5e9";
  };

  const debtorRecords = data
    ? Object.values(data.debtors).filter(
        (r) =>
          r.paymentSchedule.agingBands.total > 0 ||
          r.vatSchedule.agingBands.total > 0
      )
    : [];

  return (
    <FinanceContainer>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Button
            onClick={() => router.push("/finance/debtors")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          <Typography variant="h4">Comprehensive Aging Report (From Excel)</Typography>
        </Box>

        {/* File Path Input */}
        {!excelPath && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                📁 Excel File Path Required
              </Typography>
              <Typography variant="body2">
                Enter the full path to your debtors Excel file:
              </Typography>
            </Box>
            <TextField
              size="small"
              placeholder="e.g., C:\Users\Tutu\Downloads\PC Flats Debtors 27 March 2026.xlsx"
              value={excelPath}
              onChange={(e) => setExcelPath(e.target.value)}
              fullWidth
              sx={{ mt: 1 }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => fetchAgingReport()}
              sx={{ mt: 1 }}
            >
              Load Aging Data
            </Button>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WarningIcon fontSize="small" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {error}
                </Typography>
                <Typography variant="caption" sx={{ mt: 1, display: "block" }}>
                  <strong>File path used:</strong> {excelPath || "./data/debtors-aging.xlsx"}
                </Typography>
                <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
                  <strong>Tip:</strong> Make sure the file exists and the path is correct (Windows paths should use backslashes like: C:\Users\Name\Downloads\file.xlsx)
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setExcelPath("")}
                  sx={{ mt: 1 }}
                >
                  Change Path
                </Button>
              </Box>
            </Box>
          </Alert>
        )}

        {/* Controls */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <TextField
            type="date"
            label="As at Date"
            value={asAtDate}
            onChange={(e) => setAsAtDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadCSV}
            disabled={!data}
          >
            Export CSV
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : data ? (
          <>
            {/* Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: "#1976d2", color: "white" }}>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="caption">Total Debtors</Typography>
                    <Typography variant="h5">{data.debtorCount}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: "#388e3c", color: "white" }}>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="caption">
                      Payment Outstanding
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(data.summary.totalPaymentOutstanding)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: "#f57c00", color: "white" }}>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="caption">VAT Outstanding</Typography>
                    <Typography variant="h6">
                      {formatCurrency(data.summary.totalVatOutstanding)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ backgroundColor: "#c62828", color: "white" }}>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Typography variant="caption">Combined Outstanding</Typography>
                    <Typography variant="h6">
                      {formatCurrency(data.summary.totalCombinedOutstanding)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Show debtors with data */}
            {debtorRecords.length > 0 ? (
              <>
                {/* Tabs for Payment vs VAT Aging */}
                <Box sx={{ mb: 2 }}>
                  <Tabs
                    value={activeTab}
                    onChange={(e, newValue) => setActiveTab(newValue)}
                  >
                    <Tab label="Payment Schedule Aging" />
                    <Tab label="VAT Schedule Aging" />
                    <Tab label="Combined View" />
                  </Tabs>
                </Box>

                {/* Payment Schedule Aging Table */}
                {activeTab === 0 && (
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#1976d2" }}>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                            Debtor Ref
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            Last Balance
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            Current
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            30 Days
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            60 Days
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            90 Days
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            120+
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {debtorRecords
                          .filter((r) => r.paymentSchedule.hasData)
                          .map((record) => (
                            <TableRow key={record.debtorRef}>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                {record.debtorRef}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(
                                  record.paymentSchedule.lastBalance
                                )}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ backgroundColor: getAgingColor("Current") }}
                              >
                                {formatCurrency(
                                  record.paymentSchedule.agingBands["Current"] || 0
                                )}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ backgroundColor: getAgingColor("30 Days") }}
                              >
                                {formatCurrency(
                                  record.paymentSchedule.agingBands["30 Days"] || 0
                                )}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ backgroundColor: getAgingColor("60 Days") }}
                              >
                                {formatCurrency(
                                  record.paymentSchedule.agingBands["60 Days"] || 0
                                )}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ backgroundColor: getAgingColor("90 Days") }}
                              >
                                {formatCurrency(
                                  record.paymentSchedule.agingBands["90 Days"] || 0
                                )}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ backgroundColor: getAgingColor("120+") }}
                              >
                                {formatCurrency(
                                  record.paymentSchedule.agingBands["120+"] || 0
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* VAT Schedule Aging Table */}
                {activeTab === 1 && (
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f57c00" }}>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                            Debtor Ref
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            Last Balance
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            Current
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            30 Days
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            60 Days
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            90 Days
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            120+
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {debtorRecords
                          .filter((r) => r.vatSchedule.hasData)
                          .map((record) => (
                            <TableRow key={record.debtorRef}>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                {record.debtorRef}
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(record.vatSchedule.lastBalance)}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ backgroundColor: getAgingColor("Current") }}
                              >
                                {formatCurrency(
                                  record.vatSchedule.agingBands["Current"] || 0
                                )}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ backgroundColor: getAgingColor("30 Days") }}
                              >
                                {formatCurrency(
                                  record.vatSchedule.agingBands["30 Days"] || 0
                                )}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ backgroundColor: getAgingColor("60 Days") }}
                              >
                                {formatCurrency(
                                  record.vatSchedule.agingBands["60 Days"] || 0
                                )}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ backgroundColor: getAgingColor("90 Days") }}
                              >
                                {formatCurrency(
                                  record.vatSchedule.agingBands["90 Days"] || 0
                                )}
                              </TableCell>
                              <TableCell
                                align="right"
                                sx={{ backgroundColor: getAgingColor("120+") }}
                              >
                                {formatCurrency(
                                  record.vatSchedule.agingBands["120+"] || 0
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Combined View */}
                {activeTab === 2 && (
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#512da8" }}>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                            Debtor Ref
                          </TableCell>
                          <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                            Type
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            Last Balance
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            Current
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            30 Days
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            60 Days
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            90 Days
                          </TableCell>
                          <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                            120+
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {debtorRecords.map((record) => (
                          <React.Fragment key={record.debtorRef}>
                            {/* Payment Row */}
                            {record.paymentSchedule.hasData && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                  {record.debtorRef}
                                </TableCell>
                                <TableCell>
                                  <Chip label="Payment" size="small" color="primary" />
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(
                                    record.paymentSchedule.lastBalance
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ backgroundColor: getAgingColor("Current") }}
                                >
                                  {formatCurrency(
                                    record.paymentSchedule.agingBands[
                                      "Current"
                                    ] || 0
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ backgroundColor: getAgingColor("30 Days") }}
                                >
                                  {formatCurrency(
                                    record.paymentSchedule.agingBands["30 Days"] ||
                                      0
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ backgroundColor: getAgingColor("60 Days") }}
                                >
                                  {formatCurrency(
                                    record.paymentSchedule.agingBands["60 Days"] ||
                                      0
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ backgroundColor: getAgingColor("90 Days") }}
                                >
                                  {formatCurrency(
                                    record.paymentSchedule.agingBands["90 Days"] ||
                                      0
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ backgroundColor: getAgingColor("120+") }}
                                >
                                  {formatCurrency(
                                    record.paymentSchedule.agingBands["120+"] || 0
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                            {/* VAT Row */}
                            {record.vatSchedule.hasData && (
                              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                <TableCell sx={{ fontWeight: "bold" }}>
                                  {record.debtorRef}
                                </TableCell>
                                <TableCell>
                                  <Chip label="VAT" size="small" color="warning" />
                                </TableCell>
                                <TableCell align="right">
                                  {formatCurrency(record.vatSchedule.lastBalance)}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ backgroundColor: getAgingColor("Current") }}
                                >
                                  {formatCurrency(
                                    record.vatSchedule.agingBands["Current"] || 0
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ backgroundColor: getAgingColor("30 Days") }}
                                >
                                  {formatCurrency(
                                    record.vatSchedule.agingBands["30 Days"] || 0
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ backgroundColor: getAgingColor("60 Days") }}
                                >
                                  {formatCurrency(
                                    record.vatSchedule.agingBands["60 Days"] || 0
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ backgroundColor: getAgingColor("90 Days") }}
                                >
                                  {formatCurrency(
                                    record.vatSchedule.agingBands["90 Days"] || 0
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ backgroundColor: getAgingColor("120+") }}
                                >
                                  {formatCurrency(
                                    record.vatSchedule.agingBands["120+"] || 0
                                  )}
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            ) : (
              <Alert severity="warning">
                No debtors with aging data found in the Excel file. Ensure the
                file has the correct structure with H9:K and H51:K ranges.
              </Alert>
            )}
          </>
        ) : (
          <Typography>No aging data available</Typography>
        )}
      </Box>
    </FinanceContainer>
  );
};

export default ComprehensiveAgingReport;
